import React, { useCallback, useMemo } from 'react';
import { Flex, Switch, Text, Box, Button, Grid, GridItem, Tr, Td, useToast } from '@televet/kibble-ui';
import {
  ExclusionRuleSelectionFragment,
  useDeleteDirectBookingExclusionRuleMutation,
  useUpdateDirectBookingExclusionRuleMutation,
} from 'shared/types/graphql';
import { DaysOfWeek } from 'pages/Settings/pages/GeneralInfoSettings/OfficeHoursPicker';
import { getFormattedDateFromTimeString } from '../utils';
import ConfirmDeletePopover from './ConfirmDeletePopover';
import { getTimezoneAbbreviation } from 'shared/utils/timezoneDisplayNames';

interface ExclusionTableRowProps {
  rowIndex: number;
  exclusionRule: ExclusionRuleSelectionFragment;
  handleEditExclusion: (exclusionRule: ExclusionRuleSelectionFragment) => void;
  providerCount: number;
  appointmentTypeCount: number;
  isClinicAdmin: boolean;
  clinicTimezoneName: string;
}

interface GroupedTimeRange {
  startAt: string;
  endAt: string;
  updatedAt: string;
  daysOfWeek: number[]; // Representing days of the week as numbers (0 = Sunday, 6 = Saturday)
}

const ExclusionTableRow = ({
  rowIndex,
  exclusionRule,
  handleEditExclusion,
  providerCount,
  appointmentTypeCount,
  isClinicAdmin,
  clinicTimezoneName,
}: ExclusionTableRowProps): JSX.Element => {
  const toast = useToast();

  const [updateExclusion] = useUpdateDirectBookingExclusionRuleMutation();
  const [deleteExclusion] = useDeleteDirectBookingExclusionRuleMutation({
    refetchQueries: ['getDirectBookingExclusions'],
  });

  const groupedTimeRanges = useMemo(() => {
    const groupedByTime: Record<string, GroupedTimeRange> = {};
    exclusionRule.timeSlots.forEach((timeSlot) => {
      const key = `${timeSlot.startTime}-${timeSlot.endTime}`;

      if (!groupedByTime[key]) {
        groupedByTime[key] = {
          startAt: timeSlot.startTime,
          endAt: timeSlot.endTime,
          updatedAt: timeSlot.updatedAt,
          daysOfWeek: [timeSlot.dayOfWeek],
        };
      } else if (!groupedByTime[key].daysOfWeek.includes(timeSlot.dayOfWeek)) {
        groupedByTime[key].daysOfWeek.push(timeSlot.dayOfWeek);
      }
    });

    return Object.values(groupedByTime).sort((a, b) => b.daysOfWeek.length - a.daysOfWeek.length);
  }, [exclusionRule.timeSlots]);

  const isActiveToggleDisabled = useMemo(() => {
    return !isClinicAdmin || !exclusionRule.timeSlots.length;
  }, [exclusionRule.timeSlots.length, isClinicAdmin]);

  const handleActiveToggled = useCallback(() => {
    if (!isClinicAdmin) return;

    try {
      updateExclusion({
        variables: {
          ruleId: exclusionRule.id,
          data: { isActive: !exclusionRule.isActive },
        },
      });
    } catch (e) {
      console.error('Error updating exclusion rule', e);
      toast({ title: 'Error updating exclusion rule. Please refresh the page and try again.', status: 'error' });
    }
  }, [exclusionRule.id, exclusionRule.isActive, isClinicAdmin, toast, updateExclusion]);

  const handleDeleteRule = useCallback(async () => {
    if (!isClinicAdmin) return;

    try {
      await deleteExclusion({
        variables: {
          ruleId: exclusionRule.id,
        },
      });
      toast({ title: `The exclusion "${exclusionRule.name}" was successfully deleted.`, status: 'success' });
    } catch (e) {
      console.error('Error deleting exclusion rule', e);
      toast({ title: 'Error deleting exclusion rule. Please refresh the page and try again.', status: 'error' });
    }
  }, [deleteExclusion, exclusionRule.id, exclusionRule.name, isClinicAdmin, toast]);

  const appointmentTypeLabel = useMemo(
    () =>
      exclusionRule.appointmentTypes.length === appointmentTypeCount
        ? 'All appointment types'
        : `${exclusionRule.appointmentTypes.length} appointment types`,
    [appointmentTypeCount, exclusionRule.appointmentTypes.length],
  );

  const providerLabel = useMemo(
    () =>
      exclusionRule.providers.length === providerCount
        ? 'All providers'
        : `${exclusionRule.providers.length} providers`,
    [exclusionRule.providers.length, providerCount],
  );

  return (
    <Tr
      bg={rowIndex % 2 === 0 ? 'transparent' : 'background.lessSubtle'}
      sx={{ borderSpacing: '8px 12px' }}
      verticalAlign="top"
    >
      <Td py={3} pl={2} pr={1}>
        <Flex direction="column">
          <Text size="sm" fontWeight="bold">
            {exclusionRule.name}
          </Text>
          {exclusionRule.appointmentTypes.length === 1 ? (
            exclusionRule.appointmentTypes.map((type) => (
              <Text size="sm" key={type.id}>
                {type.name || type.id}
              </Text>
            ))
          ) : (
            <Text size="sm">{appointmentTypeLabel}</Text>
          )}
          {exclusionRule.providers.length === 1 ? (
            exclusionRule.providers.map((provider) => (
              <Text size="xs" variant="subtle" key={provider.id}>{`${provider.firstName} ${provider.lastName}`}</Text>
            ))
          ) : (
            <Text size="xs" variant="subtle">
              {providerLabel}
            </Text>
          )}
        </Flex>
      </Td>
      <Td py={3} px={1}>
        <Box>
          <Switch
            size="sm"
            label="Active"
            isChecked={exclusionRule.isActive}
            onChange={handleActiveToggled}
            labelStyle={{ fontSize: 'sm', fontWeight: 'semibold' }}
            isDisabled={isActiveToggleDisabled}
          />
        </Box>
      </Td>
      <Td py={3} px={1}>
        <Grid templateColumns="3fr 2fr">
          <GridItem>
            <Text size="sm" variant="subtle" mr={4}>
              Days
            </Text>
          </GridItem>
          <GridItem>
            <Text size="sm" variant="subtle">
              Times
            </Text>
          </GridItem>
          {groupedTimeRanges.map((slot) => (
            <React.Fragment key={`${slot.startAt}-${slot.endAt}-${slot.daysOfWeek.join(',')}`}>
              <GridItem>
                <Text size="sm" fontWeight="bold" mr={4}>
                  {slot.daysOfWeek.map((day) => DaysOfWeek[day]).join(', ')}
                </Text>
              </GridItem>
              <GridItem>
                <Text as="span" size="sm" fontWeight="bold">
                  {getFormattedDateFromTimeString(slot.startAt, clinicTimezoneName, slot.updatedAt)}-
                  {getFormattedDateFromTimeString(slot.endAt, clinicTimezoneName, slot.updatedAt)}
                </Text>
                <Text size="sm" fontWeight="semibold" mr={8} float="right">
                  {getTimezoneAbbreviation(clinicTimezoneName)}
                </Text>
              </GridItem>
            </React.Fragment>
          ))}
        </Grid>
      </Td>
      <Td py={3} pl={1} pr={2}>
        {isClinicAdmin && (
          <>
            <Button iconName="pen" variant="ghost" onClick={(): void => handleEditExclusion(exclusionRule)} />
            <ConfirmDeletePopover
              title="Delete Exclusion"
              description="Are you sure you would like to delete this exclusion rule? This action cannot be undone."
              onConfirm={handleDeleteRule}
            />
          </>
        )}
      </Td>
    </Tr>
  );
};

export default ExclusionTableRow;
